import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FlussTimelineEvent } from "modules/fluss/types/fluss";

export const FlussPageHeader = ({
    title,
    subtitle,
    status,
    actions,
}: {
    title: ReactNode;
    subtitle?: ReactNode;
    status?: ReactNode;
    actions?: ReactNode;
}) => (
    <Stack direction={{ xs: "column", md: "row" }} gap={2} justifyContent="space-between" alignItems={{ md: "center" }}>
        <Box>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h5" component="h1">{title}</Typography>
                {status}
            </Stack>
            {subtitle && <Typography color="text.secondary">{subtitle}</Typography>}
        </Box>
        {actions && <Stack direction="row" gap={1} flexWrap="wrap">{actions}</Stack>}
    </Stack>
);

const statusColors: Record<string, "default" | "primary" | "success" | "warning" | "error"> = {
    draft: "warning",
    review: "primary",
    approved: "success",
    published: "success",
    superseded: "default",
    archived: "default",
    running: "primary",
    completed: "success",
    terminated: "default",
    incident: "error",
};

export const FlussStatusChip = ({ status }: { status: string }) => {
    const { t } = useTranslation();
    return (
        <Chip
            size="small"
            color={statusColors[status] || "default"}
            label={t(`fluss.status.${status}`, { defaultValue: status === "terminated" ? "Encerrado" : status })}
        />
    );
};

export const LifecycleStepper = ({
    activeStep,
    steps,
}: {
    activeStep: number;
    steps: Array<{ key: string; fallback: string }>;
}) => {
    const { t } = useTranslation();
    return (
        <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step) => (
                <Step key={step.key}>
                    <StepLabel>{t(step.key, { defaultValue: step.fallback })}</StepLabel>
                </Step>
            ))}
        </Stepper>
    );
};

export const FilterBar = ({
    search,
    onSearch,
    children,
}: {
    search: string;
    onSearch: (value: string) => void;
    children?: ReactNode;
}) => {
    const { t } = useTranslation();
    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                <TextField
                    size="small"
                    value={search}
                    onChange={(event) => onSearch(event.target.value)}
                    label={t("fluss.common.search", { defaultValue: "Search" })}
                    fullWidth
                />
                {children}
            </Stack>
        </Paper>
    );
};

export const SidePanel = ({
    open,
    onClose,
    title,
    children,
    width = 400,
}: {
    open: boolean;
    onClose: () => void;
    title: ReactNode;
    children: ReactNode;
    width?: number;
}) => (
    <Drawer anchor="right" open={open} onClose={onClose}>
        <Box sx={{ width: { xs: "100vw", sm: width }, p: 2 }}>
            <Typography variant="h6">{title}</Typography>
            <Divider sx={{ my: 2 }} />
            {children}
        </Box>
    </Drawer>
);

export const NodeValidationBadge = ({ count }: { count: number }) => {
    const { t } = useTranslation();
    if (!count) return null;
    return (
        <Chip
            size="small"
            color="error"
            label={t("fluss.validation.issue_count", {
                count,
                defaultValue: `${count} issue(s)`,
            })}
        />
    );
};

export const EmptyState = ({
    title,
    description,
    actionLabel,
    onAction,
}: {
    title: ReactNode;
    description: ReactNode;
    actionLabel?: ReactNode;
    onAction?: () => void;
}) => (
    <Paper variant="outlined" sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6">{title}</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>{description}</Typography>
        {actionLabel && onAction && <Button onClick={onAction}>{actionLabel}</Button>}
    </Paper>
);

export const ConfirmActionDialog = ({
    open,
    title,
    description,
    confirmLabel,
    cancelLabel,
    onConfirm,
    onClose,
    busy,
    children,
    confirmDisabled,
}: {
    open: boolean;
    title: ReactNode;
    description: ReactNode;
    confirmLabel: ReactNode;
    cancelLabel: ReactNode;
    onConfirm: () => void;
    onClose: () => void;
    busy?: boolean;
    children?: ReactNode;
    confirmDisabled?: boolean;
}) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent><Typography color="text.secondary">{description}</Typography>{children}</DialogContent>
        <DialogActions>
            <Button variant="text" onClick={onClose}>{cancelLabel}</Button>
            <Button color="primary" onClick={onConfirm} disabled={busy || confirmDisabled}>{confirmLabel}</Button>
        </DialogActions>
    </Dialog>
);

export const InstanceTimeline = ({ events }: { events: FlussTimelineEvent[] }) => (
    <Stack divider={<Divider flexItem />} gap={0}>
        {events.map((event) => (
            <Box key={event.id} sx={{ py: 1.5, borderLeft: "3px solid", borderColor: `${event.tone || "neutral"}.main`, pl: 2 }}>
                <Typography fontWeight={700}>{event.label}</Typography>
                {event.detail && <Typography variant="body2" color="text.secondary">{event.detail}</Typography>}
                {event.timestamp && <Typography variant="caption" color="text.secondary">{event.timestamp}</Typography>}
            </Box>
        ))}
    </Stack>
);

export const FlussViewer = ({ children, ariaLabel }: { children: ReactNode; ariaLabel: string }) => (
    <Paper
        variant="outlined"
        role="region"
        aria-label={ariaLabel}
        sx={{ minHeight: 360, overflow: "hidden", position: "relative", bgcolor: "background.paper" }}
    >
        {children}
    </Paper>
);
